import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const OPDReportsList = ({ data = [], consultants = [], conversions = {}, filters = {} }) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredData = data.filter((item) => {
    // Basic filter logic for the summary table rows
    if (filters.opdType === "New" && item.description.includes("Follow-up")) return false;
    if (filters.opdType === "Follow-up" && item.description.includes("New")) return false;
    return true;
  });

  const totalRows = filteredData.length;
  const start = totalRows > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  const columns = [
    {
      name: "Metric Description",
      selector: (row) => row.description || "—",
      sortable: true,
      width: "300px",
      cell: (row) => <span className="text-capitalize">{row.description || "—"}</span>
    },
    {
      name: "Day",
      selector: (row) => row.day || 0,
      sortable: true,
    },
    {
      name: "Week",
      selector: (row) => row.week || 0,
      sortable: true,
    },
    {
      name: "Month",
      selector: (row) => row.month || 0,
      sortable: true,
    },
  ];

  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.text("OPD Reports", 14, 15);
    
    // Summary Data
    doc.setFontSize(12);
    doc.text("Consultant Breakdown (Monthly)", 14, 25);
    doc.setFontSize(10);
    doc.setTextColor(100);
    let yPos = 32;
    consultants.forEach((c, i) => {
      if (i < 5) { // Limit to top 5
        doc.text(`Dr. ${c.consultant_name || 'Unknown'}: ${c.total || 0}`, 14, yPos);
        yPos += 7;
      }
    });

    // OPD Conversions (using same layout as in UI)
    doc.setTextColor(0);
    doc.setFontSize(12);
    doc.text("OPD Conversions", 100, 25);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`To Residential: ${conversions.residential || 0}`, 100, 32);
    doc.text(`To DRP: ${conversions.drp || 0}`, 100, 39);
    doc.text(`To Home Visitation: ${conversions.hv || 0}`, 100, 46);
    doc.text(`To Another Unit: ${conversions.therapy || 0}`, 100, 53);
    
    // Table reset
    doc.setTextColor(0);
    const tableColumn = ["Metric Description", "Day", "Week", "Month"];
    const tableRows = filteredData.map(d => [
      d.description || "—",
      d.day || 0,
      d.week || 0,
      d.month || 0
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: Math.max(yPos + 5, 65),
    });
    doc.save("opd_report.pdf");
  };

  const getCSVData = () => {
    const headers = ["Metric Description", "Day", "Week", "Month"];
    const rows = filteredData.map(d => [
      d.description || "—",
      d.day || 0,
      d.week || 0,
      d.month || 0
    ]);

    const consultantRows = consultants.map(c => [
      `Dr. ${c.consultant_name || 'Unknown'}`,
      c.total || 0
    ]);

    return [
      ["Consultant Breakdown (Monthly)"],
      ...consultantRows,
      [], // blank line
      ["OPD Conversions"],
      ["To Residential", conversions.residential || 0],
      ["To DRP", conversions.drp || 0],
      ["To Home Visitation", conversions.hv || 0],
      ["To Another Unit", conversions.therapy || 0],
      [], // blank line
      headers,
      ...rows
    ];
  };

  const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
          <select className="form-select form-select-sm" style={{ width: "80px" }} value={rowsPerPage} onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}>
            {[5, 10, 15, 20, 25].map((option) => (<option key={option} value={option}>{option}</option>))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-1">
          <button className="btn btn-sm border" onClick={() => onChangePage(currentPage - 1, rowCount)} disabled={currentPage === 1}>
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>Page {currentPage} of {totalPages}</span>
          <button className="btn btn-sm border" onClick={() => onChangePage(currentPage + 1, rowCount)} disabled={currentPage === totalPages}>
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown">
            <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li><button className="dropdown-item" onClick={handleExportPDF}><i className="ri-file-pdf-line me-1 text-danger"></i> PDF</button></li>
              <li><CSVLink data={getCSVData()} filename="opd_report.csv" className="dropdown-item"><i className="ri-file-excel-line me-1 text-success"></i> CSV</CSVLink></li>
            </ul>
          </div>
        </div>
        <h6 className="mb-0 text-center text-muted flex-grow-1">Showing {start}-{end} of {totalRows} summaries</h6>
      </div>
      <div className="card-body p-2">
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={setRowsPerPage}
          onChangePage={setCurrentPage}
          paginationComponent={CustomPagination}
          highlightOnHover
          striped
          responsive
          noDataComponent={<div className="p-3 text-muted">No OPD data found</div>}
        />

        <div className="row mt-4">
          <div className="col-md-6">
            <div className="card shadow-sm border p-3">
              <h5 className="mb-3 border-bottom pb-2">Consultant Breakdown (Monthly)</h5>
              <ul className="list-group list-group-flush">
                {consultants.length > 0 ? (
                  consultants.map((consultant, idx) => (
                    <li key={idx} className="list-group-item d-flex justify-content-between align-items-center text-capitalize">
                      Dr. {consultant.consultant_name || 'Unknown'} 
                      <span className="badge bg-primary rounded-pill">{consultant.total || 0}</span>
                    </li>
                  ))
                ) : (
                  <li className="list-group-item text-muted text-center py-3">No consultant data</li>
                )}
              </ul>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card shadow-sm border p-3">
              <h5 className="mb-3 border-bottom pb-2">OPD Conversions</h5>
              <div className="d-flex flex-wrap gap-2 mt-2">
                <div className="p-2 border rounded bg-label-primary text-center flex-grow-1">
                  <small>To Residential</small>
                  <div className="fw-bold">{conversions.residential || 0}</div>
                </div>
                <div className="p-2 border rounded bg-label-success text-center flex-grow-1">
                  <small>To DRP</small>
                  <div className="fw-bold">{conversions.drp || 0}</div>
                </div>
                <div className="p-2 border rounded bg-label-info text-center flex-grow-1">
                  <small>To Home Visitation</small>
                  <div className="fw-bold">{conversions.hv || 0}</div>
                </div>
                <div className="p-2 border rounded bg-label-warning text-center flex-grow-1">
                  <small>To Another Unit</small>
                  <div className="fw-bold">{conversions.therapy || 0}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OPDReportsList;
